(() => {
  if (customElements.get('cbd-native-cart') || !window.HTMLDialogElement) return;
  class CBDNativeCart extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.dialog = this.querySelector('dialog');
      this.labels = JSON.parse(this.querySelector('[data-labels]').textContent);
      this.root = this.dataset.root.replace(/\/?$/, '/');
      this.preview = this.dataset.preview === 'true';
      if (window.CBDCartRewards) this.rewards = new window.CBDCartRewards(this.querySelector('[data-rewards]'), this.labels, {
        preview: this.preview, rootUrl: this.root,
        onChange: cart => { this.cart = cart; this.render(); this.announceUpdate(); },
        onBusy: busy => this.lock(busy)
      });
      this.express = this.querySelector('[data-express]');
      this.expressVersion = 0;
      this.comparePrices = new Map();
      this.express?.addEventListener('click', event => { if (!this.expressAllowed()) { event.preventDefault(); event.stopImmediatePropagation(); } }, true);
      this.querySelector('[data-checkout-form]').addEventListener('change', () => this.updateExpressState());
      this.busy = false;
      this.cart = null;
      this.querySelectorAll('[data-close]').forEach(button => button.addEventListener('click', () => this.dialog.close()));
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.dialog.close();
      });
      document.addEventListener('click', event => this.captureCartLink(event), true);
      document.addEventListener('submit', event => this.captureCartForm(event), true);
      document.addEventListener('submit', event => this.captureProductForm(event), true);
      this.querySelector('[data-checkout-form]').addEventListener('submit', async event => {
        if (this.checkoutVerified) { this.checkoutVerified = false; return; }
        event.preventDefault();
        if (this.preview || this.busy || !this.cart?.item_count) return;
        this.lock(true);
        try {
          await this.rewards.guardCheckout();
          this.checkoutVerified = true;
          this.lock(false);
          this.querySelector('[data-checkout-form]').requestSubmit(this.querySelector('[data-checkout]'));
        } catch { this.status(this.label('reward_retry')); this.lock(false); }
      });
      window.addEventListener('pageshow', () => { if (this.dialog.open) this.load(); });
      window.addEventListener('cbd:cart:open', () => this.open());
    }
    endpoint(name) { return `${this.root}cart${name ? `/${name}` : ''}.js`; }
    label(name) { return this.labels[name] || name; }
    money(amount) { return new Intl.NumberFormat(document.documentElement.lang || 'fr', { style: 'currency', currency: this.cart?.currency || 'EUR' }).format(amount / 100); }
    status(message) { this.querySelector('[data-status]').textContent = message; }
    async recoverCart() {
      let cart = this.rewards?.engine?.snapshot().cart || this.cart;
      if (!cart) { try { cart = await this.request(this.endpoint('')); } catch {} }
      if (cart) { this.cart = cart; this.render(); this.announceUpdate(); }
    }
    lock(value) {
      this.busy = value;
      this.querySelector('[data-items]').setAttribute('aria-busy', String(value));
      this.querySelectorAll('[data-mutation], [data-checkout]').forEach(button => { button.disabled = value || (button.hasAttribute('data-checkout') && !this.rewards?.canCheckout()); });
      this.querySelector('[data-rewards]').inert = value;
      this.updateExpressState();
    }
    expressAllowed() {
      const terms = this.querySelector('[data-checkout-form] input[type="checkbox"][required]');
      return !this.preview && !this.busy && this.expressLoaded && this.rewards?.canCheckout() && (!terms || terms.checked);
    }
    updateExpressState() {
      if (!this.express) return;
      this.express.inert = !this.expressAllowed();
      const terms = this.querySelector('[data-checkout-form] input[type="checkbox"][required]');
      this.express.querySelector('[data-express-note]').hidden = !terms || terms.checked || this.preview;
    }
    async refreshExpress() {
      const version = ++this.expressVersion;
      const signature = this.pricingSignature(this.cart);
      this.expressLoaded = false;
      this.updateExpressState();
      if (!this.cart?.item_count) { if (this.express) this.express.hidden = true; return; }
      try {
        const url = new URL(this.root, location.origin);
        url.searchParams.set('section_id', 'cbd-cart-express');
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
        if (!response.ok) throw new Error('express');
        const html = await response.text();
        if (version !== this.expressVersion) return;
        const document = new DOMParser().parseFromString(html, 'text/html');
        const prices = document.querySelector('[data-cbd-cart-prices]');
        if (prices) {
          const data = JSON.parse(prices.textContent);
          if (signature === this.pricingSignature(data) && signature === this.pricingSignature(this.cart)) {
            this.comparePrices = new Map(data.items.map(item => [item.key, Number(item.compare_at_price) || 0]));
            this.querySelectorAll('[data-line-price]').forEach((node, index) => this.renderLinePrice(node, this.cart.items[index]));
            this.renderSavings();
          }
        }
        if (!this.express) return;
        const wallets = document.querySelector('[data-cbd-wallets]');
        if (!wallets) { this.express.hidden = true; return; }
        this.express.querySelector('[data-express-content]').replaceChildren(document.importNode(wallets, true));
        this.express.hidden = false;
        this.expressLoaded = true;
        this.updateExpressState();
      } catch { if (this.express && version === this.expressVersion) this.express.hidden = true; }
    }
    async request(url, options = {}) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 12000);
      try {
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
        const data = await response.json();
        if (!response.ok || data.status >= 400) throw new Error(typeof data.description === 'string' ? data.description : this.label('error'));
        return data;
      } catch (error) {
        if (error.name === 'AbortError') throw new Error(this.label('error'));
        throw error;
      } finally { clearTimeout(timer); }
    }
    async load() {
      if (this.busy) return;
      this.lock(true);
      this.status(this.label('loading'));
      try {
        this.cart = this.preview ? await this.request(this.endpoint('')) : await this.rewards.refresh();
        this.render();
        this.announceUpdate();
        this.status('');
      } catch (error) { await this.recoverCart(); this.status(error.message || this.label('error')); }
      finally { this.lock(false); }
    }
    async open() {
      if (!this.dialog.open) this.dialog.showModal();
      await this.load();
    }
    captureCartLink(event) {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest('a[href]');
      if (!link || this.contains(link) || link.target === '_blank') return;
      const url = new URL(link.href, location.href);
      if (url.origin !== location.origin || url.pathname.replace(/\/$/, '') !== `${this.root}cart`.replace(/\/$/, '')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.open();
    }
    async captureProductForm(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || this.contains(form)) return;
      const url = new URL(form.action, location.href);
      if (url.origin !== location.origin || !url.pathname.match(/\/cart\/add(?:\.js)?\/?$/) || !form.querySelector('[name="id"]')) return;
      if (event.submitter?.name === 'checkout' || event.submitter?.closest('.shopify-payment-button')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.busy) return;
      if (!form.reportValidity()) return;
      this.lock(true);
      const submitter = event.submitter;
      if (submitter) submitter.disabled = true;
      if (!this.dialog.open) this.dialog.showModal();
      this.status(this.label('loading'));
      try {
        const formData = new FormData(form);
        formData.delete('sections');
        formData.delete('sections_url');
        const add = () => this.request(this.endpoint('add'), { method: 'POST', body: formData });
        if (this.preview) { await add(); this.cart = await this.request(this.endpoint('')); }
        else this.cart = await this.rewards.mutate(add);
        this.render();
        this.status(this.label('added'));
        this.announceUpdate();
      } catch (error) { await this.recoverCart(); this.status(error.message || this.label('error')); }
      finally { this.lock(false); if (submitter) submitter.disabled = false; }
    }
    captureCartForm(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || this.contains(form) || event.submitter?.name !== 'checkout') return;
      const url = new URL(form.action, location.href);
      if (url.origin !== location.origin || url.pathname.replace(/\/$/, '') !== `${this.root}cart`.replace(/\/$/, '')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.open();
    }
    async change(key, quantity) {
      if (this.busy || !Number.isInteger(quantity) || quantity < 0) return;
      this.lock(true);
      this.status(this.label('loading'));
      try {
        const change = () => this.request(this.endpoint('change'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: key, quantity }) });
        this.cart = this.preview ? await change() : await this.rewards.mutate(change);
        this.render();
        this.status(this.label('updated'));
        this.announceUpdate();
      } catch (error) { await this.recoverCart(); this.status(error.message || this.label('error')); }
      finally { this.lock(false); }
    }
    announceUpdate() {
      document.dispatchEvent(new CustomEvent('cbd:cart:updated', { detail: { itemCount: this.cart.item_count } }));
      const icon = document.getElementById('cart-icon-bubble');
      if (!icon) return;
      let bubble = icon.querySelector('.cart-count-bubble');
      if (!this.cart.item_count) { bubble?.remove(); return; }
      if (!bubble) { bubble = this.element('div', 'cart-count-bubble'); icon.append(bubble); }
      const count = this.element('span', '', String(this.cart.item_count));
      count.setAttribute('aria-hidden', 'true');
      const accessibleCount = this.element('span', 'visually-hidden', `${this.label('quantity')} : ${this.cart.item_count}`);
      bubble.replaceChildren(count, accessibleCount);
      bubble.hidden = false;
    }
    element(tag, className, text) {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (text !== undefined) node.textContent = text;
      return node;
    }
    safeProductUrl(path) {
      try { const url = new URL(path, location.href); return url.origin === location.origin ? url.href : `${this.root}cart`; }
      catch { return `${this.root}cart`; }
    }
    pricingSignature(cart) {
      return JSON.stringify([cart?.currency, ...(cart?.items || []).map(item => [item.key, item.quantity, item.original_line_price])]);
    }
    referencePrice(item) {
      const compareAt = Number(this.comparePrices.get(item.key)) || 0;
      return Math.max(item.original_line_price, compareAt * item.quantity, item.final_line_price);
    }
    savingPercent(saving, reference) {
      const value = reference > 0 ? Math.max(0, Math.min(100, saving / reference * 100)) : 0;
      return new Intl.NumberFormat(document.documentElement.lang || 'fr', {maximumFractionDigits: 1}).format(value) + ' %';
    }
    renderLinePrice(node, item) {
      if (!item) return;
      node.replaceChildren();
      const reference = this.referencePrice(item);
      const savings = reference - item.final_line_price;
      if (savings > 0) {
        const was = this.element('s', '', this.money(reference));
        was.setAttribute('aria-label', this.label('price_before') + ' ' + this.money(reference));
        node.append(was);
      }
      const current = this.element('strong', '', item.final_line_price === 0 ? this.label('free_item') : this.money(item.final_line_price));
      current.setAttribute('aria-label', this.label('price_now') + ' ' + this.money(item.final_line_price));
      node.append(current);
      if (savings > 0) node.append(this.element('span', 'cbd-cart__line-saving', this.label('line_saving').replace('[percent]', this.savingPercent(savings, reference))));
    }
    renderSavings() {
      const summary = this.querySelector('[data-savings]');
      if (!summary) return;
      const reference = this.cart.items.reduce((total, item) => total + this.referencePrice(item), 0);
      const savings = Math.max(0, reference - this.cart.total_price);
      summary.hidden = savings === 0 || !this.cart.item_count;
      summary.querySelector('[data-reference-total]').textContent = this.money(reference);
      summary.querySelector('[data-saved-total]').textContent = '−' + this.savingPercent(savings, reference);
    }
    itemNode(item) {
      const li = this.element('li', 'cbd-cart__item');
      const image = this.element('img');
      const imageUrl = item.featured_image?.url || item.image;
      if (imageUrl) {
        const url = new URL(imageUrl, location.href);
        if (url.protocol === 'https:' || url.origin === location.origin) { url.searchParams.set('width', '160'); image.src = url.href; }
      }
      image.alt = '';
      image.width = 76; image.height = 88; image.loading = 'lazy';
      li.append(image);
      const body = this.element('div');
      const title = this.element('a', '', item.product_title || item.title);
      title.href = this.safeProductUrl(item.url);
      body.append(title);
      if (item.variant_title && item.variant_title !== 'Default Title') body.append(this.element('p', 'cbd-cart__variant', item.variant_title));
      if (item.selling_plan_allocation?.selling_plan?.name) body.append(this.element('p', 'cbd-cart__variant', item.selling_plan_allocation.selling_plan.name));
      Object.entries(item.properties || {}).forEach(([name, value]) => {
        if (!name.startsWith('_') && value) body.append(this.element('p', 'cbd-cart__property', `${name} : ${value}`));
      });
      const bottom = this.element('div', 'cbd-cart__line-bottom');
      const controls = this.element('div', 'cbd-cart__quantity');
      const input = this.element('input');
      input.type = 'number'; input.min = '0'; input.step = '1'; input.value = item.quantity; input.inputMode = 'numeric';
      input.setAttribute('aria-label', `${this.label('quantity')} : ${item.product_title || item.title}`);
      input.dataset.mutation = '';
      input.addEventListener('change', () => { const quantity = Number(input.value); if (Number.isInteger(quantity) && quantity >= 0) this.change(item.key, quantity); else input.value = item.quantity; });
      [-1, 1].forEach(delta => {
        const button = this.element('button', '', delta < 0 ? '−' : '+');
        button.type = 'button'; button.dataset.mutation = '';
        button.setAttribute('aria-label', `${this.label(delta < 0 ? 'decrease' : 'increase')} : ${item.product_title || item.title}`);
        button.addEventListener('click', () => this.change(item.key, item.quantity + delta));
        if (delta < 0) controls.append(button, input); else controls.append(button);
      });
      const price = this.element('div', 'cbd-cart__price');
      price.dataset.linePrice = '';
      this.renderLinePrice(price, item);
      bottom.append(controls, price);
      body.append(bottom);
      const remove = this.element('button', 'cbd-cart__remove', this.label('remove'));
      remove.type = 'button'; remove.dataset.mutation = '';
      remove.addEventListener('click', () => this.change(item.key, 0));
      body.append(remove);
      li.append(body);
      return li;
    }
    render() {
      if (this.rewards) this.rewards.render(this.cart);
      this.querySelector('[data-items]').replaceChildren(...this.cart.items.map(item => this.itemNode(item)));
      this.querySelector('[data-empty]').hidden = Boolean(this.cart.item_count);
      this.querySelector('[data-empty-link]').hidden = Boolean(this.cart.item_count);
      this.refreshExpress();
      this.querySelector('[data-footer]').hidden = !this.cart.item_count;
      this.querySelector('[data-total]').textContent = this.money(this.cart.total_price);
      this.renderSavings();
      const discounts = this.querySelector('[data-discounts]');
      discounts.replaceChildren(...(this.cart.cart_level_discount_applications || []).map(discount => this.element('p', '', `${discount.title} : −${this.money(discount.total_allocated_amount)}`)));
      const shipping = this.querySelector('[data-shipping]');
      shipping.hidden = this.dataset.country !== 'FR' || this.cart.currency !== 'EUR' || !this.cart.items.some(item => item.requires_shipping) || !this.cart.item_count;
      if (!shipping.hidden) {
        const left = Math.max(0, 5000 - this.cart.total_price);
        shipping.querySelector('[data-shipping-text]').textContent = left ? this.label('shipping_remaining').replace('[amount]', this.money(left)) : this.label('shipping_unlocked');
        shipping.querySelector('progress').value = Math.min(5000, this.cart.total_price);
      }
    }
  }
  customElements.define('cbd-native-cart', CBDNativeCart);
})();


